/* Price breakup — presentation, toggle behaviour and per-variant refresh.

   Kept in its own asset so the core PDP script (cart, variants, delivery) is
   not rewritten for a display detail.

   Four jobs:
     1. restyle the panel as a price table. The theme renders it with
        .pdp-specifications, a multi-column grid meant for spec pairs, so the
        rows flow across and Total is stranded on a line of its own.
     2. slide the panel open and shut instead of snapping
     3. flip the label between "View breakup" and "Hide breakup"
     4. refresh the panel when the variant changes — gold and making charges
        move with karat, so a stale panel would contradict the price above it

   Styles are injected from here rather than shipped as a stylesheet so the
   integration stays one file and one include. The panel starts hidden, so
   there is no unstyled flash to worry about.
*/

(() => {
  if (window.CaratKinBreakup) return;
  window.CaratKinBreakup = true;

  const original = document.querySelector('[data-breakup-toggle]');
  const panel = document.querySelector('[data-pdp-breakup]');
  if (!original || !panel) return;

  const OPEN_LABEL = original.dataset.labelOpen || original.textContent.trim() || 'View breakup';
  const CLOSE_LABEL = original.dataset.labelClose || 'Hide breakup';
  const HEADING = panel.dataset.heading || 'Price Breakup';
  const NOTE = panel.dataset.note ||
    'This is an estimated price. Actual price may differ as per actual weights.';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DURATION = 320;

  /* ---- Styles -----------------------------------------------------------
     Scoped under .ck-breakup-styled, a class added below, so every rule
     out-specifies the theme's own .pdp-specifications grid without !important.
  */
  const CSS = `
.pdp-breakup.ck-breakup-styled {
  margin: 0.5rem 0 0.7rem;
  padding: 1.1rem 1.25rem 1rem;
  border: 1px solid rgba(23, 77, 52, 0.14);
  border-radius: 14px;
  background: #fcfbf9;
  width: 100%; /* edge to edge with the price line and Add to cart */
}
.pdp-breakup.ck-breakup-styled .ck-breakup__heading {
  margin: 0 0 0.35rem;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 500;
  color: #5f5a52;
}
.pdp-breakup.ck-breakup-styled .pdp-specifications {
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  gap: 0;
}
/* One part per row: label + detail on the left, charged over struck on the right. */
.pdp-breakup.ck-breakup-styled .pdp-specifications > div {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  padding: 0.8rem 0;
  border: 0;
  border-bottom: 1px solid rgba(23, 20, 15, 0.09);
  background: none;
  border-radius: 0;
  text-align: left;
  min-height: 10px;
}
.pdp-breakup.ck-breakup-styled .pdp-specifications > div > dt,
.pdp-breakup.ck-breakup-styled .pdp-specifications > div > dd {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin: 0;
  opacity: 1;
}
.pdp-breakup.ck-breakup-styled .pdp-specifications > div > dd {
  align-items: flex-end;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.pdp-breakup.ck-breakup-styled .ck-row__label {
  font-size: 0.95rem;
  line-height: 1.35;
  font-weight: 500;
  color: #17140f;
}
.pdp-breakup.ck-breakup-styled .ck-row__detail {
  font-size: 0.8rem;
  line-height: 1.35;
  color: #7a746b;
}
.pdp-breakup.ck-breakup-styled .ck-row__now {
  font-size: 0.95rem;
  line-height: 1.35;
  font-weight: 500;
  color: #17140f;
}
.pdp-breakup.ck-breakup-styled .ck-row__was {
  font-size: 0.8rem;
  line-height: 1.35;
  color: #9a948b;
}
/* Total: bigger and bolder, with the struck full price under it. */
.pdp-breakup.ck-breakup-styled .ck-row--total {
  padding-top: 1rem;
}
.pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__label,
.pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__now {
  font-size: 1.2rem;
  font-weight: 700;
}
.pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__was {
  font-size: 0.85rem;
}
.pdp-breakup.ck-breakup-styled .ck-breakup__note {
  margin: 0.85rem 0 0;
  font-size: 0.76rem;
  line-height: 1.5;
  color: #5f5a52;
}
@media only screen and (max-width: 767px) {
  .pdp-breakup.ck-breakup-styled {
    width: 100%;
    padding: 1rem;
  }
  /* Phone sizes follow the rest of the product page (13px labels, 11px detail). */
  .pdp-breakup.ck-breakup-styled .ck-breakup__heading { font-size: 10px; }
  .pdp-breakup.ck-breakup-styled .pdp-specifications > div { padding: 0.6rem 0; }
  .pdp-breakup.ck-breakup-styled .ck-row__label,
  .pdp-breakup.ck-breakup-styled .ck-row__now { font-size: 13px; }
  .pdp-breakup.ck-breakup-styled .ck-row__detail,
  .pdp-breakup.ck-breakup-styled .ck-row__was { font-size: 11px; }
  .pdp-breakup.ck-breakup-styled .ck-row--total { padding-top: 0.75rem; }
  .pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__label,
  .pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__now { font-size: 15px; }
  .pdp-breakup.ck-breakup-styled .ck-row--total .ck-row__was { font-size: 11px; }
  .pdp-breakup.ck-breakup-styled .ck-breakup__note { font-size: 11px; }
}`;

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  /** Add the heading and footnote the theme's markup does not include. */
  function decorate() {
    panel.classList.add('ck-breakup-styled');

    const list = panel.querySelector('.pdp-specifications');
    if (list && !panel.querySelector('.ck-breakup__heading')) {
      const h = document.createElement('p');
      h.className = 'ck-breakup__heading';
      h.textContent = HEADING;
      list.parentNode.insertBefore(h, list);
    }
    if (list && !panel.querySelector('.ck-breakup__note')) {
      const note = document.createElement('p');
      note.className = 'ck-breakup__note';
      note.textContent = NOTE;
      list.parentNode.insertBefore(note, list.nextSibling);
    }
  }

  decorate();

  /* ---- Toggle ---------------------------------------------------------- */

  // pdp-main.js binds its own handler that flips `hidden` directly, which would
  // fight the animation. Replacing the node drops that listener; cloning keeps
  // the theme's classes and styling.
  const toggle = original.cloneNode(true);
  toggle.textContent = OPEN_LABEL;
  original.parentNode.replaceChild(toggle, original);

  if (!panel.id) panel.id = 'ck-breakup-panel';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', panel.id);
  panel.style.overflow = 'hidden';

  let settle = null;
  const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

  function setOpen(open) {
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? CLOSE_LABEL : OPEN_LABEL;
    clearTimeout(settle);

    if (reduced) {
      panel.hidden = !open;
      panel.style.maxHeight = '';
      panel.style.opacity = '';
      return;
    }

    panel.style.transition = `max-height ${DURATION}ms ease, opacity 220ms ease`;

    if (open) {
      panel.hidden = false;
      panel.style.maxHeight = '0px';
      panel.style.opacity = '0';
      // Force layout so the transition has a start value. requestAnimationFrame
      // would also work but never fires in a background tab, which would leave
      // the panel open at zero height.
      void panel.offsetHeight;
      panel.style.maxHeight = panel.scrollHeight + 'px';
      panel.style.opacity = '1';
      // Release the cap once open so the panel can grow if its contents change.
      settle = setTimeout(() => { if (isOpen()) panel.style.maxHeight = 'none'; }, DURATION + 40);
    } else {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      void panel.offsetHeight;
      panel.style.maxHeight = '0px';
      panel.style.opacity = '0';
      settle = setTimeout(() => { if (!isOpen()) panel.hidden = true; }, DURATION + 40);
    }
  }

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    setOpen(!isOpen());
  });

  /* ---- Per-variant refresh ---------------------------------------------
     The breakup is rendered server-side from a variant metafield, so another
     variant's figures can only come from re-rendering. Shopify's section
     rendering returns just this section rather than the whole page.
  */

  const wrapper = panel.closest('[id^="shopify-section-"]');
  const sectionId = wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  let inFlight = 0;

  async function refresh(variantId) {
    if (!sectionId || !variantId) return;
    const ticket = ++inFlight;
    try {
      const url =
        window.location.pathname +
        '?variant=' + encodeURIComponent(variantId) +
        '&section_id=' + encodeURIComponent(sectionId);
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-pdp-breakup]');
      // A slower earlier request must not overwrite a newer one.
      if (!next || ticket !== inFlight) return;
      panel.innerHTML = next.innerHTML;
      decorate(); // innerHTML replacement drops the heading and note
      if (isOpen()) panel.style.maxHeight = 'none';
    } catch {
      /* leave the existing figures rather than blanking the panel */
    }
  }

  // pdp-main.js rewrites the cart button's variant id on every option change,
  // which is the one signal that fires for every option group.
  const cartBtn = document.querySelector('[data-pdp-cart]');
  if (cartBtn) {
    let last = cartBtn.dataset.variantId;
    new MutationObserver(() => {
      const id = cartBtn.dataset.variantId;
      if (id && id !== last) {
        last = id;
        refresh(id);
      }
    }).observe(cartBtn, { attributes: true, attributeFilter: ['data-variant-id'] });
  }
})();
